import { Injectable } from '@angular/core';
import {
  CityParkingCards,
  ParkingCardSummary,
  ParkingVehicleInfoCard,
} from '../../models/parking-card.model';
import {
  ParkingCardChartItem,
  ParkingCardDashboardStat,
  ParkingCardDashboardView,
  ParkingCardDetailDto,
  ParkingCardMarketingDashboardDto,
  ParkingCardMarketingDashboardView,
  ParkingCardRecentActivityItem,
  ParkingCardTableDto,
  ParkingCardUserCandidateDto,
  VerifyParkingCardUserDto,
  ParkingCardTaiwanSyncStatus,
  ParkingCardTaiwanSyncLogRow,
  ParkingCardTaiwanSyncLogTable,
  ParkingCardPendingTaiwanSyncRow,
  ParkingCardPendingTaiwanSyncTable,
} from '../../models/parking/parking-card-list.model';

@Injectable({
  providedIn: 'root',
})
export class MapperParkingCardListService {
  mapTableFromDto(dto: ParkingCardTableDto): {
    items: CityParkingCards[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as ParkingCardTableDto & Record<string, unknown>;
    return {
      items: ((dto.data ?? raw['Data'] ?? []) as CityParkingCards[]).map(item => this.normalizeCardItem(item)),
      total: dto.total ?? (raw['Total'] as number) ?? 0,
      page: dto.page ?? (raw['Page'] as number) ?? 1,
      pageSize: dto.pageSize ?? (raw['PageSize'] as number) ?? 20,
      totalPages: dto.totalPages ?? (raw['TotalPages'] as number) ?? 0,
    };
  }

  mapMarketingDashboard(dto: ParkingCardMarketingDashboardDto): ParkingCardMarketingDashboardView {
    const raw = dto as ParkingCardMarketingDashboardDto & Record<string, unknown>;
    const summaryRaw = dto.summary ?? raw['Summary'];
    const ratiosRaw = dto.ratios ?? raw['Ratios'];
    const recentRaw = (dto.recentActivity ?? raw['RecentActivity'] ?? []) as ParkingCardRecentActivityItem[];
    const chartRaw = (dto.chartItems ?? raw['ChartItems'] ?? []) as ParkingCardChartItem[];

    const dashboardView = this.mapDashboardView(summaryRaw as ParkingCardSummary);
    const ratiosAny = ratiosRaw as unknown as Record<string, number> | undefined;

    return {
      stats: dashboardView.stats,
      ratios: {
        activePercent: ratiosRaw?.activePercent ?? ratiosAny?.['ActivePercent'] ?? dashboardView.ratios.activePercent,
        expiredPercent: ratiosRaw?.expiredPercent ?? ratiosAny?.['ExpiredPercent'] ?? dashboardView.ratios.expiredPercent,
        cancelledPercent: ratiosRaw?.cancelledPercent ?? ratiosAny?.['CancelledPercent'] ?? dashboardView.ratios.cancelledPercent,
      },
      recentActivity: recentRaw.map(item => this.mapRecentActivityItem(item)),
      chartItems: chartRaw.map(item => this.normalizeChartItem(item)),
    };
  }

  mapSearchField(type: 'card_code' | 'card_name' | 'name' | 'uid' | 'license'): string {
    switch (type) {
      case 'card_name':
        return 'card_name';
      case 'name':
        return 'name';
      case 'uid':
        return 'uid';
      case 'license':
        return 'license';
      default:
        return 'cardcode';
    }
  }

  mapStatusFilter(filterStatus: number): 'all' | 'active' | 'expired' | 'cancelled' | 'suspended' | undefined {
    switch (filterStatus) {
      case 1:
        return 'active';
      case 2:
        return 'expired';
      case 3:
        return 'cancelled';
      case 4:
        return 'suspended';
      default:
        return undefined;
    }
  }

  mapDashboardStats(summary: ParkingCardSummary | null | undefined): ParkingCardDashboardStat[] {
    return this.mapDashboardView(summary).stats;
  }

  mapDashboardView(summary: ParkingCardSummary | null | undefined): ParkingCardDashboardView {
    const safe = this.normalizeSummaryInput(summary);
    const total = safe.totalCard || 0;
    const ratios = {
      activePercent: total > 0 ? Math.round((safe.totalNormalCard / total) * 100) : 0,
      expiredPercent: total > 0 ? Math.round((safe.totalExpireCard / total) * 100) : 0,
      cancelledPercent: total > 0 ? Math.round((safe.totalCancelCard / total) * 100) : 0,
    };

    return {
      stats: [
        {
          title: 'ข้อมูลรวมทั้งหมด',
          subtitle: '',
          value: (safe.totalCard || 0).toLocaleString(),
          icon: 'style',
          color: 'text-blue-600',
        },
        {
          title: 'ใช้งานอยู่',
          subtitle: '',
          value: (safe.totalNormalCard || 0).toLocaleString(),
          icon: 'check_circle',
          color: 'text-emerald-500',
        },
        {
          title: 'หมดอายุ',
          subtitle: '',
          value: (safe.totalExpireCard || 0).toLocaleString(),
          icon: 'history',
          color: 'text-amber-500',
        },
        {
          title: 'ยกเลิก',
          subtitle: '',
          value: (safe.totalCancelCard || 0).toLocaleString(),
          icon: 'cancel',
          color: 'text-rose-500',
        },
      ],
      ratios,
    };
  }

  private normalizeSummaryInput(summary: ParkingCardSummary | null | undefined): ParkingCardSummary {
    if (!summary) {
      return this.emptySummary();
    }
    return this.normalizeSummary(summary);
  }

  mapVerifyUser(dto: VerifyParkingCardUserDto): {
    verified: boolean;
    requiresSelection: boolean;
    candidates: ParkingCardUserCandidateDto[];
    user: ParkingCardUserCandidateDto | null;
  } {
    const raw = dto as VerifyParkingCardUserDto & Record<string, unknown>;
    const candidates = (dto.candidates ?? raw['Candidates'] ?? []) as ParkingCardUserCandidateDto[];
    const user = (dto.user ?? raw['User'] ?? null) as ParkingCardUserCandidateDto | null;

    return {
      verified: dto.verified ?? raw['Verified'] === true,
      requiresSelection: dto.requiresSelection ?? raw['RequiresSelection'] === true,
      candidates: candidates.map(item => this.normalizeCandidate(item)),
      user: user ? this.normalizeCandidate(user) : null,
    };
  }

  mapDetailFromDto(dto: ParkingCardDetailDto): ParkingCardDetailDto {
    const raw = dto as any;
    return {
      id: dto.id ?? raw.Id ?? 0,
      cardCode: dto.cardCode ?? raw.CardCode ?? '',
      cardName: dto.cardName ?? raw.CardName ?? '',
      name: dto.name ?? raw.Name ?? '',
      address: dto.address ?? raw.Address ?? '',
      telephone: dto.telephone ?? raw.Telephone ?? '',
      uid: dto.uid ?? raw.Uid ?? '',
      cpUserId: dto.cpUserId ?? raw.CpUserId ?? null,
      siteId: dto.siteId ?? raw.SiteId ?? 0,
      siteName: dto.siteName ?? raw.SiteName ?? '',
      cardTypeId: dto.cardTypeId ?? raw.CardTypeId ?? 0,
      cardTypeName: dto.cardTypeName ?? raw.CardTypeName ?? '',
      cardMode: dto.cardMode ?? raw.CardMode ?? 1,
      issueDate: dto.issueDate ?? raw.IssueDate ?? null,
      expireDate: dto.expireDate ?? raw.ExpireDate ?? null,
      subscriptionId: dto.subscriptionId ?? raw.SubscriptionId ?? 0,
      rateId: dto.rateId ?? raw.RateId ?? 0,
      gateGroupId: dto.gateGroupId ?? raw.GateGroupId ?? 0,
      clientId: dto.clientId ?? raw.ClientId ?? null,
      clientName: dto.clientName ?? raw.ClientName ?? '',
      vehicles: (dto.vehicles ?? raw.Vehicles ?? []).map((item: any) => ({
        vehicleId: item.vehicleId ?? item.VehicleId ?? 0,
        plateNumber: item.plateNumber ?? item.PlateNumber ?? '',
        provinceId: item.provinceId ?? item.ProvinceId ?? 0,
        province: item.province ?? item.Province ?? '',
        active: item.active ?? item.Active ?? true,
        cityParkingVehicleId: item.cityParkingVehicleId ?? item.CityParkingVehicleId ?? null,
      })),
    };
  }

  mapCardModeToStatus(cardMode: number): string {
    switch (cardMode) {
      case 2:
        return 'ยกเลิก';
      case 3:
        return 'ถูกระงับ';
      default:
        return 'ปกติ';
    }
  }

  mapStatusToCardMode(status: string): number {
    switch (status) {
      case 'ยกเลิก':
        return 2;
      case 'ถูกระงับ':
        return 3;
      default:
        return 1;
    }
  }

  resolveDisplayStatus(item: CityParkingCards): number {
    const expireRaw = item.expireDate;
    if (expireRaw) {
      const expire = new Date(expireRaw);
      if (!isNaN(expire.getTime()) && expire.getTime() < Date.now()) {
        return 0;
      }
    }
    const status = item.status ?? 0;
    return status === 0 ? 0 : status;
  }

  private normalizeCardItem(item: CityParkingCards): CityParkingCards {
    const raw = item as CityParkingCards & Record<string, unknown>;
    const licenseRaw = item.licenseList ?? raw['LicenseList'] ?? [];
    const normalized: CityParkingCards = {
      id: item.id ?? raw['Id'] ?? 0,
      cardCode: item.cardCode ?? raw['CardCode'] ?? '',
      cardName: item.cardName ?? raw['CardName'] ?? '',
      name: item.name ?? raw['Name'] ?? '',
      issueDate: String(item.issueDate ?? raw['IssueDate'] ?? ''),
      expireDate: String(item.expireDate ?? raw['ExpireDate'] ?? ''),
      createDate: String(item.createDate ?? raw['CreateDate'] ?? ''),
      writeDate: String(item.writeDate ?? raw['WriteDate'] ?? ''),
      remark: String(item.remark ?? raw['remark'] ?? raw['Remarks'] ?? raw['remarks'] ?? ''),
      siteId: item.siteId ?? raw['SiteId'] ?? 0,
      siteName: item.siteName ?? raw['SiteName'] ?? '',
      cardTypeName: item.cardTypeName ?? raw['CardTypeName'] ?? '',
      subscriptionName: String(item.subscriptionName ?? raw['SubscriptionName'] ?? ''),
      vehicleTypeId: item.vehicleTypeId ?? (raw['VehicleTypeId'] as number | null) ?? null,
      vehicleTypeName: item.vehicleTypeName ?? (raw['VehicleTypeName'] as string) ?? '',
      uid: item.uid ?? raw['Uid'] ?? '',
      status: item.status ?? raw['Status'] ?? 0,
      clientId: item.clientId ?? (raw['ClientId'] as number | null) ?? null,
      clientName: item.clientName ?? (raw['ClientName'] as string) ?? '',
      licenseList: (licenseRaw as unknown[]).map((v) => this.normalizeLicenseItem(v)),
    };
    normalized.status = this.resolveDisplayStatus(normalized);
    return normalized;
  }

  private normalizeLicenseItem(item: unknown): ParkingVehicleInfoCard {
    const v = (item ?? {}) as ParkingVehicleInfoCard & Record<string, unknown>;
    return {
      vehicleId: v.vehicleId ?? v['VehicleId'] ?? 0,
      plateNumber: String(v.plateNumber ?? v['PlateNumber'] ?? ''),
      province: String(v.province ?? v['Province'] ?? ''),
    };
  }

  private normalizeSummary(summary: ParkingCardSummary): ParkingCardSummary {
    const raw = summary as ParkingCardSummary & Record<string, unknown> & Record<string, number>;
    const anyRaw = summary as any;
    return {
      totalCard: summary.totalCard ?? anyRaw.TotalCard ?? raw['TotalCard'] ?? 0,
      totalNormalCard: summary.totalNormalCard ?? anyRaw.TotalNormalCard ?? raw['TotalNormalCard'] ?? 0,
      totalCancelCard: summary.totalCancelCard ?? anyRaw.TotalCancelCard ?? raw['TotalCancelCard'] ?? 0,
      totalSuspendCard: summary.totalSuspendCard ?? anyRaw.TotalSuspendCard ?? raw['TotalSuspendCard'] ?? 0,
      totalExpireCard: summary.totalExpireCard ?? anyRaw.TotalExpireCard ?? raw['TotalExpireCard'] ?? 0,
    };
  }

  private normalizeCandidate(item: ParkingCardUserCandidateDto): ParkingCardUserCandidateDto {
    const raw = item as ParkingCardUserCandidateDto & Record<string, unknown>;
    return {
      cpUserId: item.cpUserId ?? raw['CpUserId'] ?? 0,
      lineId: item.lineId ?? raw['LineId'] ?? '',
      fullName: item.fullName ?? raw['FullName'] ?? '',
      phone: item.phone ?? raw['Phone'] ?? '',
    };
  }

  private emptySummary(): ParkingCardSummary {
    return {
      totalCard: 0,
      totalNormalCard: 0,
      totalCancelCard: 0,
      totalSuspendCard: 0,
      totalExpireCard: 0,
    };
  }

  private mapRecentActivityItem(item: ParkingCardRecentActivityItem) {
    const raw = item as ParkingCardRecentActivityItem & Record<string, unknown>;
    const cardName = item.cardName ?? (raw['CardName'] as string) ?? '';
    const name = item.name ?? (raw['Name'] as string) ?? '';
    const createDate = item.createDate ?? (raw['CreateDate'] as string) ?? null;
    const id = item.id ?? (raw['Id'] as number) ?? 0;
    const displayName = cardName || name || 'ไม่ระบุชื่อ';

    return {
      id,
      initial: displayName.charAt(0).toUpperCase(),
      name: displayName,
      timeAgo: '',
      info: name && cardName && name !== cardName ? name : `รหัส #${id}`,
      color: '',
      createDate,
    };
  }

  private normalizeChartItem(item: ParkingCardChartItem): ParkingCardChartItem {
    const raw = item as ParkingCardChartItem & Record<string, unknown>;
    return {
      id: item.id ?? (raw['Id'] as number) ?? 0,
      cardName: item.cardName ?? (raw['CardName'] as string) ?? '',
      name: item.name ?? (raw['Name'] as string) ?? '',
      createDate: String(item.createDate ?? raw['CreateDate'] ?? ''),
      issueDate: String(item.issueDate ?? raw['IssueDate'] ?? ''),
    };
  }

  mapTaiwanSyncStatus(res: unknown): ParkingCardTaiwanSyncStatus {
    const r = (res ?? {}) as Record<string, unknown>;
    return {
      parkingCardId: Number(r['parkingCardId'] ?? r['ParkingCardId'] ?? 0),
      cardCode: (r['cardCode'] ?? r['CardCode'] ?? null) as string | null,
      siteId: Number(r['siteId'] ?? r['SiteId'] ?? 0),
      siteName: (r['siteName'] ?? r['SiteName'] ?? null) as string | null,
      taiwanSite: !!(r['taiwanSite'] ?? r['TaiwanSite']),
      hasTaiwanUrl: !!(r['hasTaiwanUrl'] ?? r['HasTaiwanUrl']),
      taiwanSyncStatus: (r['taiwanSyncStatus'] ?? r['TaiwanSyncStatus'] ?? null) as string | null,
      taiwanLastSyncAt: (r['taiwanLastSyncAt'] ?? r['TaiwanLastSyncAt'] ?? null) as string | null,
      taiwanLastSyncError: (r['taiwanLastSyncError'] ?? r['TaiwanLastSyncError'] ?? null) as string | null,
      taiwanId: (r['taiwanId'] ?? r['TaiwanId'] ?? null) as string | null,
      taiwanCardNo: (r['taiwanCardNo'] ?? r['TaiwanCardNo'] ?? null) as string | null,
      allocatedCardNo: (r['allocatedCardNo'] ?? r['AllocatedCardNo'] ?? null) as string | null,
      identify: String(r['identify'] ?? r['Identify'] ?? 'general'),
      canResync: !!(r['canResync'] ?? r['CanResync']),
    };
  }

  mapTaiwanSyncLogTable(res: unknown): ParkingCardTaiwanSyncLogTable {
    const raw = (res ?? {}) as Record<string, unknown>;
    const list = (raw['data'] ?? raw['Data'] ?? []) as Record<string, unknown>[];
    const data: ParkingCardTaiwanSyncLogRow[] = list.map((r) => ({
      id: Number(r['id'] ?? r['Id'] ?? 0),
      parkingCardId: (r['parkingCardId'] ?? r['ParkingCardId'] ?? null) as number | null,
      siteId: (r['siteId'] ?? r['SiteId'] ?? null) as number | null,
      taiwanSite: (r['taiwanSite'] ?? r['TaiwanSite'] ?? null) as boolean | null,
      syncAction: (r['syncAction'] ?? r['SyncAction'] ?? null) as string | null,
      triggerType: (r['triggerType'] ?? r['TriggerType'] ?? null) as string | null,
      status: (r['status'] ?? r['Status'] ?? null) as string | null,
      changeNote: (r['changeNote'] ?? r['ChangeNote'] ?? null) as string | null,
      requestPayload: (r['requestPayload'] ?? r['RequestPayload'] ?? null) as string | null,
      responsePayload: (r['responsePayload'] ?? r['ResponsePayload'] ?? null) as string | null,
      errorMessage: (r['errorMessage'] ?? r['ErrorMessage'] ?? null) as string | null,
      taiwanIdAfter: (r['taiwanIdAfter'] ?? r['TaiwanIdAfter'] ?? null) as string | null,
      taiwanCardNoAfter: (r['taiwanCardNoAfter'] ?? r['TaiwanCardNoAfter'] ?? null) as string | null,
      triggeredByUid: (r['triggeredByUid'] ?? r['TriggeredByUid'] ?? null) as number | null,
      triggeredByLogin: (r['triggeredByLogin'] ?? r['TriggeredByLogin'] ?? null) as string | null,
      attemptNo: (r['attemptNo'] ?? r['AttemptNo'] ?? null) as number | null,
      createDate: (r['createDate'] ?? r['CreateDate'] ?? null) as string | null,
    }));
    const total = Number(raw['total'] ?? raw['Total'] ?? data.length);
    const page = Number(raw['page'] ?? raw['Page'] ?? 1);
    const pageSize = Number(raw['pageSize'] ?? raw['PageSize'] ?? 20);
    return {
      data,
      total,
      page,
      pageSize,
      totalPages: Number(raw['totalPages'] ?? raw['TotalPages'] ?? (total === 0 ? 0 : Math.ceil(total / pageSize))),
    };
  }

  mapPendingTaiwanSyncTable(res: unknown): ParkingCardPendingTaiwanSyncTable {
    const raw = (res ?? {}) as Record<string, unknown>;
    const list = (raw['data'] ?? raw['Data'] ?? []) as Record<string, unknown>[];
    const data: ParkingCardPendingTaiwanSyncRow[] = list.map((r) => ({
      id: Number(r['id'] ?? r['Id'] ?? 0),
      cardCode: (r['cardCode'] ?? r['CardCode'] ?? null) as string | null,
      name: (r['name'] ?? r['Name'] ?? null) as string | null,
      uid: (r['uid'] ?? r['Uid'] ?? null) as string | null,
      clientId: (r['clientId'] ?? r['ClientId'] ?? null) as number | null,
      clientName: (r['clientName'] ?? r['ClientName'] ?? null) as string | null,
      issueDate: (r['issueDate'] ?? r['IssueDate'] ?? null) as string | null,
      expireDate: (r['expireDate'] ?? r['ExpireDate'] ?? null) as string | null,
      licensePlate: (r['licensePlate'] ?? r['LicensePlate'] ?? null) as string | null,
      taiwanSyncStatus: (r['taiwanSyncStatus'] ?? r['TaiwanSyncStatus'] ?? null) as string | null,
      siteId: Number(r['siteId'] ?? r['SiteId'] ?? 0),
      siteName: (r['siteName'] ?? r['SiteName'] ?? null) as string | null,
    }));
    const total = Number(raw['total'] ?? raw['Total'] ?? data.length);
    const page = Number(raw['page'] ?? raw['Page'] ?? 1);
    const pageSize = Number(raw['pageSize'] ?? raw['PageSize'] ?? 20);
    return {
      data,
      total,
      page,
      pageSize,
      totalPages: Number(raw['totalPages'] ?? raw['TotalPages'] ?? (total === 0 ? 0 : Math.ceil(total / pageSize))),
      taiwanSite: !!(raw['taiwanSite'] ?? raw['TaiwanSite']),
      hasTaiwanUrl: !!(raw['hasTaiwanUrl'] ?? raw['HasTaiwanUrl']),
    };
  }
}

type ParkingCardDetailVehicleDto = ParkingCardDetailDto['vehicles'][number];
